import { assertTokenValidForMutation, handleResponse } from "./service-helpers";

const NBH_MEMBERS_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/members`;
const NBH_SOCIAL_ACCOUNT_TRANSACTIONS_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/social-account/transactions`;

export type SocialAccountTransactionReason =
    | "ORDER_DONATION"
    | "VOLUNTARY_DONATION"
    | "RESIGNATION_TRANSFER"
    | "ORDER_PAYMENT"
    | "IMPORT";

export type SocialAccountTransactionDirection = "CREDIT" | "DEBIT";

export interface SocialAccountStatementEntry {
    id: string;
    reason: SocialAccountTransactionReason;
    direction: SocialAccountTransactionDirection;
    amount: number;
    description: string;
    transactionTimestamp: string;
    bookedAt: string;
    bookedBy: string;
    bookedByName: string | null;
    orderId: string | null;
    orderNumber: string | null;
    memberId: string | null;
    memberNumber: number | null;
    memberName: string | null;
    memberResigned: boolean;
    reversalOf: string | null;
}

export interface SocialAccountStatement {
    openingBalance: number;
    closingBalance: number;
    from: string | null;
    to: string | null;
    search: string | null;
    entries: SocialAccountStatementEntry[];
}

export interface StatementDateRange {
    from: string;
    to: string;
}

function statementQuery(range?: StatementDateRange | null, search?: string | null): string {
    const params = new URLSearchParams();
    if (range) {
        params.set('from', range.from);
        params.set('to', range.to);
    }
    if (search && search.trim()) {
        params.set('search', search.trim());
    }
    const query = params.toString();
    return query ? `?${query}` : '';
}

export async function getSocialAccountStatement(
    token: string,
    range?: StatementDateRange | null,
    search?: string | null,
): Promise<SocialAccountStatement> {
    const response = await fetch(`${NBH_SOCIAL_ACCOUNT_TRANSACTIONS_URL}${statementQuery(range, search)}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return await handleResponse<SocialAccountStatement>(response) as SocialAccountStatement;
}

async function downloadStatementFile(
    token: string,
    format: 'excel' | 'pdf',
    fallbackFilename: string,
    range?: StatementDateRange | null,
    search?: string | null,
): Promise<{ blob: Blob; filename: string }> {
    const response = await fetch(`${NBH_SOCIAL_ACCOUNT_TRANSACTIONS_URL}/${format}${statementQuery(range, search)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
        throw new Error(`Social account ${format} export failed`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
    return { blob, filename: filenameMatch?.[1] ?? fallbackFilename };
}

export function downloadSocialAccountStatementExcel(
    token: string,
    range?: StatementDateRange | null,
    search?: string | null,
): Promise<{ blob: Blob; filename: string }> {
    return downloadStatementFile(token, 'excel', 'sozialkonto.xlsx', range, search);
}

export function downloadSocialAccountStatementPdf(
    token: string,
    range?: StatementDateRange | null,
    search?: string | null,
): Promise<{ blob: Blob; filename: string }> {
    return downloadStatementFile(token, 'pdf', 'sozialkonto.pdf', range, search);
}

export interface VoluntaryPointDonationResult {
    memberBalanceAfter: number;
    socialAccountBalanceAfter: number;
}

export async function donatePointsToSocialAccount(
    token: string | null,
    memberId: string,
    amount: number,
    note?: string,
): Promise<VoluntaryPointDonationResult | void> {
    if (!token) {
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation();

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/point-donations`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount, note: note ?? null })
    });

    return handleResponse<VoluntaryPointDonationResult>(response);
}

export interface ReverseVoluntaryDonationResult {
    memberName: string;
    reversedPoints: number;
    memberBalanceAfter: number;
    socialAccountBalanceAfter: number;
}

export async function reverseVoluntaryDonation(
    token: string | null,
    transactionId: string,
): Promise<ReverseVoluntaryDonationResult | void> {
    if (!token) {
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation();

    const response = await fetch(`${NBH_SOCIAL_ACCOUNT_TRANSACTIONS_URL}/${transactionId}/reverse`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return handleResponse<ReverseVoluntaryDonationResult>(response);
}
